import {
  Box,
  Breadcrumbs,
  Card,
  CircularProgress,
  FormLabel,
  Grid,
  Input,
  Stack,
  Textarea,
  Typography,
} from '@mui/joy';
import { CreateEditIncomeModal } from '@/components/Pages/Incomes/CreateEditIncomeModal';
import { createFileRoute, Link, Navigate } from '@tanstack/react-router';
import { pageTitle } from '@/utils/pageTitle';
import { DeleteIncomeModal } from '@/components/Pages/Incomes/DeleteIncomeModal';
import { OpenInNew as OpenInNewIcon } from '@mui/icons-material';
import { useMemo } from 'react';
import { formatDateUTC } from '@/utils/date';
import { useIncome } from '@/hooks/useIncomes';
import { useAuth } from '@/contexts/AuthContext';

export const Route = createFileRoute('/_protected/incomes/$id')({
  component: IncomeDetailPage,
  head: ({ params }) => pageTitle(`Ingresos / ${params.id}`),
});

function IncomeDetailPage() {
  const { id } = Route.useParams();
  const { hasClaim, isLoading: authLoading } = useAuth();
  const canRead = hasClaim('INCOMES_READ');
  const canWrite = hasClaim('INCOMES_WRITE');

  const { data: income, isLoading } = useIncome(id);

  const showActions = useMemo(() => {
    return income?.supplierId === undefined && canWrite;
  }, [income, canWrite]);

  const referenceLink = useMemo(() => {
    if (income?.clientId) {
      return (
        <Link to={`/sells/${income.referenceId}`.toString()}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            Ir a venta <OpenInNewIcon />
          </Box>
        </Link>
      );
    }

    return null;
  }, [income]);

  if (authLoading || isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!canRead) {
    return <Navigate to='/incomes' />;
  }

  if (!income) {
    return <div>Ingreso no encontrado</div>;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Stack direction='row' justifyContent='space-between'>
        <Breadcrumbs size='sm'>
          <Link to='/incomes'>Ingresos</Link>
          <Typography>{income.id}</Typography>
        </Breadcrumbs>

        {showActions && (
          <Stack direction='row' gap={1}>
            <CreateEditIncomeModal income={income} />
            <DeleteIncomeModal id={income.id.toString()} />
          </Stack>
        )}
      </Stack>

      <Card>
        <Grid container spacing={{ xs: 1, sm: 2 }}>
          {referenceLink && <Grid xs={12}>{referenceLink}</Grid>}

          <Grid xs={12} md={6}>
            <FormLabel>Fecha</FormLabel>
            <Input
              value={formatDateUTC(income.date)}
              readOnly
              variant='soft'
              size='sm'
              sx={{ backgroundColor: 'background.body' }}
            />
          </Grid>
          <Grid xs={12} md={6}>
            <FormLabel>Cuenta</FormLabel>
            <Input
              value={income.accountName}
              readOnly
              variant='soft'
              size='sm'
              sx={{ backgroundColor: 'background.body' }}
            />
          </Grid>
          <Grid xs={12} md={6}>
            <FormLabel>Categoría</FormLabel>
            <Input
              value={income.categoryName || '-'}
              readOnly
              variant='soft'
              size='sm'
              sx={{ backgroundColor: 'background.body' }}
            />
          </Grid>
          <Grid xs={12} md={6}>
            <FormLabel>Monto</FormLabel>
            <Input
              value={income.amount.toLocaleString('es-AR', {
                minimumFractionDigits: 2,
                style: 'currency',
                currency: 'ARS',
              })}
              readOnly
              variant='soft'
              size='sm'
              sx={{ backgroundColor: 'background.body' }}
            />
          </Grid>

          <Grid xs={12}>
            <FormLabel>Descripción</FormLabel>
            <Textarea
              value={income.description ?? ''}
              readOnly
              minRows={3}
              maxRows={6}
            />
          </Grid>
        </Grid>
      </Card>
    </Box>
  );
}
